import type { ReactNode } from 'react';

import classNames from 'classnames';

import styles from './Meter.module.css';

import { Text } from '../Text';
import { MeterSvg } from './MeterSvg';

type IntrinsicDivProps = JSX.IntrinsicElements['div'];

export interface MeterProps extends IntrinsicDivProps {
  /** Children will be positioned absolute and centered in the meter */
  children: ReactNode;
  /** Label for the empty side of the meter */
  emptyLabel?: ReactNode;
  /** Label for the full side of the meter */
  fullLabel?: ReactNode;
  /** Max number range */
  max: number;
  /** Current number value */
  current: number;
}

/**
 * An SVG meter used to reflect current progress against a maximum number range.
 *
 * Use `aria-label` or `aria-labelledby` to provide an accessible name to the
 * meter.
 *
 * @see https://corgi-x.tfd.engineering/components/meter
 */
export function Meter({
  children,
  emptyLabel,
  fullLabel,
  max,
  current,
  className,
  ...props
}: MeterProps) {
  return (
    <div
      role="meter"
      className={classNames(styles.container, className)}
      {...props}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={current}
    >
      <div className={styles.children}>{children}</div>
      <MeterSvg fillPercentage={current / max} />
      <div className={styles.labels}>
        <Text variant="heading-12">{emptyLabel}</Text>
        <Text variant="heading-12">{fullLabel}</Text>
      </div>
    </div>
  );
}
